import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/badge/badge.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/card/card.js';
import '@awesome.me/webawesome/dist/components/drawer/drawer.js';
import '@awesome.me/webawesome/dist/components/icon/icon.js';

import './pages/bank-accounts-page.js';
import './pages/bank-page.js';
import './pages/clients-page.js';
import './pages/contracts-page.js';
import './pages/documents-page.js';
import './pages/employees-page.js';
import './pages/invoice-page.js';
import './pages/invoices-page.js';
import './pages/timesheets-page.js';
import type { Me } from './api.js';
import { navigate } from './router.js';
import { getTheme, isDark, setTheme, type Theme } from './theme.js';

type Auth = { status: 'loading' } | { status: 'anonymous' } | { status: 'signed-in'; me: Me };

const routes = [
  { path: '/employees', label: 'Employees' },
  { path: '/bank-accounts', label: 'Bank accounts' },
  { path: '/bank', label: 'Bank' },
  { path: '/clients', label: 'Clients' },
  { path: '/contracts', label: 'Contracts' },
  { path: '/timesheets', label: 'Timesheets' },
  { path: '/invoices', label: 'Invoices' },
  { path: '/documents', label: 'Documents' },
] as const;

@customElement('app-shell')
export class AppShell extends LitElement {
  static styles = css`
    :host {
      display: grid;
      /* minmax(0, 1fr): the column never grows past the viewport because of
         a wide child (the nav scrolls instead). */
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: auto 1fr;
      min-height: 100vh;
    }

    header {
      min-width: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--wa-space-s) var(--wa-space-m);
      padding: calc(var(--wa-space-s) + env(safe-area-inset-top)) calc(var(--wa-space-l) + env(safe-area-inset-right)) var(--wa-space-s)
        calc(var(--wa-space-l) + env(safe-area-inset-left));
      border-bottom: 1px solid var(--wa-color-surface-border);
      background: var(--wa-color-surface-default, #fff);
    }

    h1 {
      margin: 0;
      margin-right: var(--wa-space-m);
      font-size: var(--wa-font-size-l);
    }

    nav {
      display: flex;
      gap: var(--wa-space-xs);
      overflow-x: auto;
      scrollbar-width: none;
      -webkit-overflow-scrolling: touch;
    }

    nav::-webkit-scrollbar {
      display: none;
    }

    nav a {
      padding: var(--wa-space-xs) var(--wa-space-s);
      border-radius: var(--wa-border-radius-m);
      color: var(--wa-color-text-normal);
      text-decoration: none;
      white-space: nowrap;
    }

    nav a:hover {
      background: var(--wa-color-neutral-fill-quiet);
    }

    nav a[aria-current='page'] {
      background: var(--wa-color-brand-fill-quiet);
      color: var(--wa-color-brand-on-quiet);
    }

    .spacer {
      flex: 1;
    }

    .user {
      color: var(--wa-color-text-quiet);
      font-size: var(--wa-font-size-s);
    }

    main {
      padding: var(--wa-space-l) calc(var(--wa-space-l) + env(safe-area-inset-right)) calc(var(--wa-space-l) + env(safe-area-inset-bottom))
        calc(var(--wa-space-l) + env(safe-area-inset-left));
      max-width: 1400px;
      width: 100%;
      box-sizing: border-box;
      margin: 0 auto;
      min-width: 0;
    }

    @media (max-width: 1500px) {
      .user .email {
        display: none;
      }
    }

    .menu-button {
      display: none;
    }

    /* The slide-in menu for tablets and phones. */
    .drawer-nav {
      display: flex;
      flex-direction: column;
      gap: var(--wa-space-2xs);
    }

    .drawer-nav a {
      padding: var(--wa-space-s) var(--wa-space-m);
      border-radius: var(--wa-border-radius-m);
      color: var(--wa-color-text-normal);
      text-decoration: none;
      font-size: var(--wa-font-size-m);
    }

    .drawer-nav a[aria-current='page'] {
      background: var(--wa-color-brand-fill-quiet);
      color: var(--wa-color-brand-on-quiet);
    }

    .drawer-user {
      display: flex;
      flex-direction: column;
      gap: var(--wa-space-s);
      color: var(--wa-color-text-quiet);
      font-size: var(--wa-font-size-s);
    }

    .drawer-actions {
      display: flex;
      align-items: center;
      gap: var(--wa-space-s);
    }

    @media (max-width: 900px) {
      header {
        padding: calc(var(--wa-space-s) + env(safe-area-inset-top)) calc(var(--wa-space-m) + env(safe-area-inset-right)) var(--wa-space-s)
          calc(var(--wa-space-m) + env(safe-area-inset-left));
      }

      /* The inline menu and the user line give way to the hamburger. */
      nav,
      .user,
      .sign-out,
      header .theme-button {
        display: none;
      }

      .menu-button {
        display: inline-flex;
      }

      main {
        padding: var(--wa-space-m) calc(var(--wa-space-m) + env(safe-area-inset-right)) calc(var(--wa-space-m) + env(safe-area-inset-bottom))
          calc(var(--wa-space-m) + env(safe-area-inset-left));
      }
    }
  `;

  @state() private auth: Auth = { status: 'loading' };
  @state() private path = window.location.pathname;
  @state() private menuOpen = false;
  @state() private theme: Theme = getTheme();

  private onPopState = () => {
    this.path = window.location.pathname;
  };

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.onPopState);
    void this.loadMe();
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    super.disconnectedCallback();
  }

  // system -> light -> dark -> system
  private cycleTheme() {
    const next: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' };
    this.theme = next[this.theme];
    setTheme(this.theme);
  }

  private renderThemeButton(size: 'small' | 'medium' = 'small') {
    const labels: Record<Theme, string> = { system: 'Theme: system', light: 'Theme: light', dark: 'Theme: dark' };
    const icon = this.theme === 'system' ? 'circle-half-stroke' : isDark() ? 'moon' : 'sun';
    return html`
      <wa-button class="theme-button" appearance="plain" size=${size} title=${labels[this.theme]} @click=${this.cycleTheme}>
        <wa-icon name=${icon} label=${labels[this.theme]}></wa-icon>
      </wa-button>
    `;
  }

  private isCurrent(path: string): boolean {
    return this.path === path || this.path.startsWith(path + '/') || this.path.startsWith(path + '?');
  }

  private navigate(e: MouseEvent, path: string) {
    e.preventDefault();
    navigate(path);
  }

  private async loadMe() {
    try {
      const res = await fetch('/api/me');
      if (res.ok) {
        this.auth = { status: 'signed-in', me: (await res.json()) as Me };
        if (this.path === '/' || this.path === '') {
          history.replaceState(null, '', routes[0].path);
          this.path = routes[0].path;
        }
      } else {
        this.auth = { status: 'anonymous' };
      }
    } catch {
      this.auth = { status: 'anonymous' };
    }
  }

  private async logout() {
    await fetch('/auth/logout', { method: 'POST' });
    this.auth = { status: 'anonymous' };
  }

  render() {
    const signedIn = this.auth.status === 'signed-in';
    return html`
      <header>
        <h1>Internal PSA</h1>
        ${signedIn
          ? html`
              <nav>
                ${routes.map(
                  (r) => html`
                    <a
                      href=${r.path}
                      aria-current=${this.isCurrent(r.path) ? 'page' : nothing}
                      @click=${(e: MouseEvent) => this.navigate(e, r.path)}
                      >${r.label}</a
                    >
                  `,
                )}
              </nav>
            `
          : nothing}
        <span class="spacer"></span>
        ${this.renderUser()}
        ${signedIn
          ? html`
              <wa-button class="menu-button" appearance="plain" size="small" @click=${() => (this.menuOpen = true)}>
                <wa-icon name="bars" label="Menu"></wa-icon>
              </wa-button>
            `
          : nothing}
      </header>
      <main>${this.renderMain()}</main>
      ${signedIn ? this.renderDrawer() : nothing}
    `;
  }

  // The same links as the inline menu, in a drawer for narrow screens.
  private renderDrawer() {
    const me = this.auth.status === 'signed-in' ? this.auth.me : null;
    return html`
      <wa-drawer label="Internal PSA" placement="start" ?open=${this.menuOpen} @wa-after-hide=${(e: Event) => { if (e.target === e.currentTarget) this.menuOpen = false; }} style="--size: 18rem;">
        <nav class="drawer-nav">
          ${routes.map(
            (r) => html`
              <a
                href=${r.path}
                aria-current=${this.isCurrent(r.path) ? 'page' : nothing}
                @click=${(e: MouseEvent) => {
                  this.menuOpen = false;
                  this.navigate(e, r.path);
                }}
                >${r.label}</a
              >
            `,
          )}
        </nav>
        <div slot="footer" class="drawer-user">
          ${me ? html`<span>${me.name}<br />${me.email}</span>` : nothing}
          <div class="drawer-actions">
            ${this.renderThemeButton('medium')}
            <wa-button size="small" appearance="outlined" @click=${this.logout}>Sign out</wa-button>
          </div>
        </div>
      </wa-drawer>
    `;
  }

  private renderUser() {
    switch (this.auth.status) {
      case 'signed-in':
        return html`
          <span class="user">${this.auth.me.name}<span class="email"> · ${this.auth.me.email}</span></span>
          ${this.renderThemeButton()}
          <wa-button class="sign-out" size="small" appearance="outlined" @click=${this.logout}>Sign out</wa-button>
        `;
      case 'anonymous':
        return html`<wa-button size="small" variant="brand" href="/auth/login">Sign in with Google</wa-button>`;
      default:
        return nothing;
    }
  }

  private renderMain() {
    if (this.auth.status === 'loading') return nothing;
    if (this.auth.status === 'anonymous') {
      return html`
        <wa-card>
          <strong slot="header">Sign in</strong>
          <p>Use your company Google Workspace account. Only managers can sign in during phase 1.</p>
          <wa-button slot="footer" variant="brand" href="/auth/login">Sign in with Google</wa-button>
        </wa-card>
      `;
    }
    if (this.path.startsWith('/clients')) return html`<clients-page></clients-page>`;
    if (this.path.startsWith('/contracts')) return html`<contracts-page></contracts-page>`;
    if (this.path.startsWith('/documents')) return html`<documents-page></documents-page>`;
    if (this.path.startsWith('/timesheets')) return html`<timesheets-page></timesheets-page>`;
    const invoice = /^\/invoices\/(\d+)/.exec(this.path);
    if (invoice) return html`<invoice-page .invoiceId=${Number(invoice[1])}></invoice-page>`;
    if (this.path.startsWith('/invoices')) return html`<invoices-page></invoices-page>`;
    if (this.path.startsWith('/bank-accounts')) return html`<bank-accounts-page></bank-accounts-page>`;
    if (this.path.startsWith('/bank')) return html`<bank-page></bank-page>`;
    return html`<employees-page></employees-page>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-shell': AppShell;
  }
}
